/**
 * @fileoverview Root Application Component
 * @description Main App component with routing, authentication, and Alaska Airlines styling
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider } from '@/context';
import { ProtectedRoute } from '@/components';
import { MainLayout } from '@/components/layout/MainLayout';
import { Dashboard } from '@/features';
import { TestCalendar } from '@/features';
import { PnrGenerator } from '@/features';
import { ModelContextProtocol } from '@/features';
import { IncidentAnalysis } from '@/features';
import { GitHubUserMetrics } from '@/features';
import { TestAutomationLeverage } from '@/features';
import { LinesOfCode } from '@/features';
import { PipelineAnalysis } from '@/features';
import { ProjectSavingsReport } from '@/features';
import { ReportLogs } from '@/features';
import { Reports } from '@/features';
import { RegressionOptimizer } from '@/features';
import { ZamlaReport } from '@/features';
import { CopilotCost } from '@/features';
import { PortfolioDashboard } from '@/features';
import { DeadheadPnr } from '@/features';
import { AITestDataGeneration } from '@/features';
import { FlightCrewDeadletter } from '@/features';
import { ServiceBusExplorer } from '@/features';
import { FlightCrewCompare } from '@/features';
import { CrewPipelineCheck } from '@/features';
import { AzureFunctionHealth } from '@/features';
import { AIEvaluationResults, ScoutEval } from '@/features';
import { Login, Callback, Home } from '@/pages';
import { MODULES } from '@/constants';
import './telemetry';
import AccessRequestPage from '@/features/AccessRequest/pages/AccessRequestPage';
import AdminRBACPage from '@/features/AdminRBAC/pages/AdminRBACPage';
import { AppInsightsPage } from '@/features/AppInsights';
import './App.css';

// Create Alaska Airlines themed MUI theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#01426A', // Alaska Navy
      light: '#0097A9', // Alaska Teal
      dark: '#002F5F',
    },
    secondary: {
      main: '#0097A9', // Alaska Teal
    },
    success: {
      main: '#00AF66',
    },
    warning: {
      main: '#FFB81C',
    },
    error: {
      main: '#E31837',
    },
    background: {
      default: '#F9FAFB',
      paper: '#ffffff',
    },
    text: {
      primary: '#1F2937',
      secondary: '#4B5563',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      color: '#01426A',
    },
    h2: {
      fontWeight: 700,
      color: '#01426A',
    },
    h3: {
      fontWeight: 600,
      color: '#01426A',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
  },
});

/**
 * Root App component with routing
 * @returns {JSX.Element} App component
 */
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <div className="app">
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/callback" element={<Callback />} />
              
              {/* Protected routes with MainLayout */}
              <Route
                element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Home />} />
                <Route
                  path="/github-metrics"
                  element={
                    <ProtectedRoute requiredModule={MODULES.GITHUB_METRICS}>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/test-calendar"
                  element={
                    <ProtectedRoute requiredModule={MODULES.TEST_CALENDAR}>
                      <TestCalendar />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/pnr-generator"
                  element={
                    <ProtectedRoute requiredModule={MODULES.PNR_GENERATOR}>
                      <PnrGenerator />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/model-context-protocol"
                  element={
                    <ProtectedRoute requiredModule={MODULES.MODEL_CONTEXT_PROTOCOL}>
                      <ModelContextProtocol />
                    </ProtectedRoute>
                  }
                />
                <Route path="/access-request" element={<AccessRequestPage />} />
                <Route
                  path="/incident-analysis"
                  element={
                    <ProtectedRoute requiredModule={MODULES.INCIDENT_ANALYSIS}>
                      <IncidentAnalysis />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/github-user-metrics"
                  element={
                    <ProtectedRoute requiredModule={MODULES.GITHUB_USER_METRICS}>
                      <GitHubUserMetrics />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ai-leverage"
                  element={
                    <ProtectedRoute requiredModule={MODULES.TEST_AUTOMATION_LEVERAGE}>
                      <TestAutomationLeverage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/copilot-cost"
                  element={
                    <ProtectedRoute requiredModule={MODULES.COPILOT_COST}>
                      <CopilotCost />
                    </ProtectedRoute>
                  }
                />
                <Route path="/pipeline-analysis" element={<ProtectedRoute requiredModule={MODULES.PIPELINE_SERVICE}><PipelineAnalysis /></ProtectedRoute>} />
                <Route path="/pipeline-service" element={<ProtectedRoute requiredModule={MODULES.PIPELINE_SERVICE}><PipelineAnalysis /></ProtectedRoute>} />
                <Route path="/project-savings-report" element={<ProtectedRoute requiredModule={MODULES.PIPELINE_SERVICE}><ProjectSavingsReport /></ProtectedRoute>} />
                <Route
                  path="/lines-of-code"
                  element={
                    <ProtectedRoute requiredModule={MODULES.LINES_OF_CODE}>
                      <LinesOfCode />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/report-logs"
                  element={
                    <ProtectedRoute requiredModule={MODULES.REPORT_LOGS}>
                      <ReportLogs />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute requiredModule={MODULES.REPORTS}>
                      <Reports />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/regression-optimizer"
                  element={
                    <ProtectedRoute requiredModule={MODULES.REGRESSION_OPTIMIZER}>
                      <RegressionOptimizer />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/zamla-report"
                  element={
                    <ProtectedRoute requiredModule={MODULES.ZAMLA_REPORT}>
                      <ZamlaReport />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/portfolio/:teamId"
                  element={
                    <PortfolioDashboard />
                  }
                />
                <Route
                  path="/deadhead-pnr"
                  element={
                    <ProtectedRoute requiredModule={MODULES.DEADHEAD_PNR}>
                      <DeadheadPnr />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ai-test-data-generation"
                  element={
                    <ProtectedRoute requiredModule={MODULES.AI_TEST_DATA_GENERATION}>
                      <AITestDataGeneration />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/flight-crew-deadletter"
                  element={
                    <ProtectedRoute requiredModule={MODULES.FLIGHT_CREW_DEADLETTER}>
                      <FlightCrewDeadletter />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/servicebus-explorer"
                  element={
                    <ProtectedRoute requiredModule={MODULES.SERVICEBUS_EXPLORER}>
                      <ServiceBusExplorer />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/flight-crew-compare"
                  element={
                    <ProtectedRoute requiredModule={MODULES.FLIGHT_CREW_COMPARE}>
                      <FlightCrewCompare />
                    </ProtectedRoute>
                  }
                />
                {/* Azure Function Health module added by CoPilot AI Agent */}
                <Route
                  path="/crew-pipeline-check"
                  element={
                    <ProtectedRoute requiredModule={MODULES.CREW_PIPELINE_CHECK}>
                      <CrewPipelineCheck />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/azure-function-health"
                  element={
                    <ProtectedRoute requiredModule={MODULES.AZURE_FUNCTION_HEALTH}>
                      <AzureFunctionHealth />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/ai-evaluation-results/*"
                  element={
                    <ProtectedRoute requiredModule={MODULES.AI_EVALUATION_RESULTS}>
                      <AIEvaluationResults />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/scout-eval/*"
                  element={
                    <ProtectedRoute requiredModule={MODULES.AI_EVALUATION_RESULTS}>
                      <ScoutEval />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/rbac"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <AdminRBACPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/app-insights"
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <AppInsightsPage />
                    </ProtectedRoute>
                  }
                />
              </Route>
              
              {/* Legacy dashboard route - redirect to github-metrics */}
              <Route
                path="/dashboard"
                element={<Navigate to="/github-metrics" replace />}
              />
              
              {/* Catch all - redirect to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
